import type { I18n } from '@lingui/core';
import type { MessageDescriptor } from '@lingui/core';
import { errorMessage } from '../i18n/errorMessages';
import { msg } from '@lingui/core/macro';
import { Trans, useLingui } from '@lingui/react/macro';
// InvitationsInbox.tsx — CM-4.2 owner-side inbox for cross-org agent
// invitations. Lists pending invitations for agents the caller owns and
// surfaces 同意 / 拒绝 quick actions that PATCH the CM-4.1 endpoint and
// reload the list. On approve, optionally jumps to the joined channel
// (CM-4.2c — relies on the server-side AddChannelMember side-effect from
// CM-4.1).
//
// Out of scope here: BPP frame, system message DM, offline detection
// (CM-4.3 / CM-4.3b). This is the "inbox card" surface only — paired
// with the simpler bell button in Sidebar.

import React, { useCallback, useEffect, useState } from 'react';
import { useChannelStore } from '../stores';
import {
  listAgentInvitations,
  decideAgentInvitation,
  ApiError,
  type AgentInvitation,
} from '../lib/api';
import { useInvitationFrames } from '../hooks/useWsHubFrames';

interface Props {
  onBack?: () => void;
  /**
   * Called when an approval succeeds and the resulting channel is one the
   * caller is already a member of. Lets the host App switch focus to the
   * channel without coupling this component to the AppContext reducer.
   */
  onJumpToChannel?: (channelId: string) => void;
}

type Filter = 'pending' | 'all';

export default function InvitationsInbox({ onBack, onJumpToChannel }: Props) {
  const { t, i18n } = useLingui();
  const loadChannels = useChannelStore((s) => s.loadChannels);
  const [invitations, setInvitations] = useState<AgentInvitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<Filter>('pending');
  const [errorMsg, setErrorMsg] = useState<MessageDescriptor | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErrorMsg(null);
    try {
      const data = await listAgentInvitations('owner');
      setInvitations(data);
    } catch (err) {
      setErrorMsg(errorMessage(err, msg`Could not load data.`));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useInvitationFrames({
    onPending: load,
    onDecided: load,
  });

  const handleDecision = useCallback(
    async (inv: AgentInvitation, decision: 'approved' | 'rejected') => {
      if (busyId) return;
      setBusyId(inv.id);
      setErrorMsg(null);
      try {
        const updated = await decideAgentInvitation(inv.id, decision);
        // Optimistic local replace — server is the source of truth, but
        // we want the row to flip state immediately even if the next
        // list reload is delayed.
        setInvitations((prev) => prev.map((p) => (p.id === inv.id ? updated : p)));

        if (decision === 'approved') {
          // The server's CM-4.1 handler already idempotent-joined the
          // agent. Reload channels so the sidebar reflects the new
          // membership, then jump unconditionally — server authz is
          // the gatekeeper, and `state.channels` here would be the
          // pre-await stale closure (find() races with the reload).
          await loadChannels();
          if (onJumpToChannel) {
            onJumpToChannel(updated.channel_id);
          }
        }
      } catch (err) {
        if (err instanceof ApiError && err.status === 409) {
          setErrorMsg(
            msg`This invitation has already been handled or its status has changed. Please refresh.`,
          );
        } else {
          setErrorMsg(errorMessage(err, msg`Could not complete the action.`));
        }
      } finally {
        setBusyId(null);
      }
    },
    [busyId, loadChannels, onJumpToChannel],
  );

  const visible =
    filter === 'pending' ? invitations.filter((inv) => inv.state === 'pending') : invitations;

  return (
    <div className="agent-page">
      <div className="admin-section-header">
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {onBack && (
            <button className="btn btn-sm" onClick={onBack}>
              <Trans>← Back</Trans>
            </button>
          )}
          <h2>
            <Trans>Agent invitations</Trans>
          </h2>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            className={`btn btn-sm${filter === 'pending' ? ' btn-primary' : ''}`}
            onClick={() => setFilter('pending')}
          >
            <Trans>Pending</Trans>
          </button>
          <button
            className={`btn btn-sm${filter === 'all' ? ' btn-primary' : ''}`}
            onClick={() => setFilter('all')}
          >
            <Trans>All</Trans>
          </button>
          <button className="btn btn-sm" onClick={load} disabled={loading}>
            <Trans>Refresh</Trans>
          </button>
        </div>
      </div>

      {errorMsg && (
        <div className="admin-error" role="alert" style={{ margin: '8px 0' }}>
          {i18n._(errorMsg)}
        </div>
      )}

      {loading ? (
        <div className="app-loading">
          <div className="loading-spinner-large" />
        </div>
      ) : visible.length === 0 ? (
        <p style={{ color: 'var(--text-secondary)', textAlign: 'center', marginTop: 40 }}>
          {filter === 'pending' ? t`No pending invitations` : t`No invitations`}
        </p>
      ) : (
        <ul className="invitation-list" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {visible.map((inv) => (
            <InvitationCard
              key={inv.id}
              invitation={inv}
              busy={busyId === inv.id}
              onApprove={() => handleDecision(inv, 'approved')}
              onReject={() => handleDecision(inv, 'rejected')}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

interface CardProps {
  invitation: AgentInvitation;
  busy: boolean;
  onApprove: () => void;
  onReject: () => void;
}

function InvitationCard({ invitation, busy, onApprove, onReject }: CardProps) {
  const { t, i18n } = useLingui();
  const isPending = invitation.state === 'pending';
  const stateLabel = i18n._(stateToLabel(invitation.state));

  return (
    <li
      className="invitation-card"
      style={{
        border: '1px solid var(--border-color)',
        borderRadius: 6,
        padding: 12,
        marginBottom: 8,
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
        <div>
          <Trans>
            <strong>Invite agent</strong>{' '}
            <span title={invitation.agent_id}>{invitation.agent_name || invitation.agent_id}</span>{' '}
            to channel{' '}
            <span title={invitation.channel_id}>
              {invitation.channel_name ? `#${invitation.channel_name}` : invitation.channel_id}
            </span>
          </Trans>
        </div>
        <span className={`invitation-state invitation-state-${invitation.state}`}>
          {stateLabel}
        </span>
      </div>
      <div style={{ color: 'var(--text-secondary)', fontSize: 12 }}>
        <Trans>
          Invited by{' '}
          <span title={invitation.requested_by}>
            {invitation.requester_name || invitation.requested_by}
          </span>
        </Trans>{' '}
        <Trans>· Created {formatTs(i18n, invitation.created_at)}</Trans>
        {invitation.decided_at !== undefined && (
          <>
            <Trans> · Handled {formatTs(i18n, invitation.decided_at)}</Trans>
          </>
        )}
        {invitation.expires_at !== undefined && (
          <>
            <Trans> · Expires {formatTs(i18n, invitation.expires_at)}</Trans>
          </>
        )}
      </div>
      {isPending && (
        <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
          <button className="btn btn-primary btn-sm" onClick={onApprove} disabled={busy}>
            {busy ? t`Processing…` : t`Accept`}
          </button>
          <button className="btn btn-sm" onClick={onReject} disabled={busy}>
            <Trans>Decline</Trans>
          </button>
        </div>
      )}
    </li>
  );
}

export function stateToLabel(state: AgentInvitation['state']): MessageDescriptor {
  switch (state) {
    case 'pending':
      return msg`Pending`;
    case 'approved':
      return msg`Accepted`;
    case 'rejected':
      return msg`Declined`;
    case 'expired':
      return msg`Expired`;
  }
}

function formatTs(i18n: I18n, ms: number): string {
  if (!ms) return '-';
  const d = new Date(ms);
  if (Number.isNaN(d.getTime())) return '-';
  return i18n.date(d, { dateStyle: 'medium', timeStyle: 'short' });
}
